import { useEffect, useState } from 'react';
import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { Label } from '~/components/ui/label';
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group';
import { useFamilyStripePaymentMethodsQuery } from '~/pages/FamilyDetail/useFamilyStripePaymentMethodsQuery';
import { formatCurrency, formatDate } from '~/utils/paymentUtils';
import { AddPaymentMethodButton } from './AddPaymentMethodButton';
import { describePaymentMethod, type SavedPaymentMethod } from './describePaymentMethod';
import type { InvoiceData } from './ParentInvoicesList';
import { useInvoicePaymentQuery } from './useInvoicePaymentQuery';
import { usePayInvoiceWithSavedMethod } from './usePayInvoiceWithSavedMethod';

/** What the dialog shows of the invoice it pays. */
export type PayableInvoice = Pick<InvoiceData, 'invoiceId' | 'amountCents' | 'memo' | 'dueDate' | 'lineItems'>;

interface PayInvoiceDialogProps {
  invoice: PayableInvoice | null;
  familyEnrollmentId?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPaid?: () => void;
}

// Finding 8: the dialog shows the invoice the way Stripe does — memo, one line
// per student, total, due date — so a parent knows what they are paying for
// before they confirm. Ruling 30 adds the other half: the parent picks a saved
// method every time, and the card fee for that method is shown before they
// confirm, never after. With only one saved method there is nothing to pick, so
// it starts chosen and priced.
export function PayInvoiceDialog({ invoice, familyEnrollmentId, open, onOpenChange, onPaid }: PayInvoiceDialogProps) {
  const [selectedMethodId, setSelectedMethodId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [chargedCents, setChargedCents] = useState<number | null>(null);

  const { data, loading: methodsLoading } = useFamilyStripePaymentMethodsQuery(
    familyEnrollmentId ?? undefined,
    { skip: !open },
  );
  const methods = (data?.familyStripePaymentMethods ?? []) as SavedPaymentMethod[];

  // A method the family no longer has is not a choice.
  const onlyMethodId = methods.length === 1 ? methods[0].paymentMethodId : null;
  const activeMethodId = methods.some((method) => method.paymentMethodId === selectedMethodId)
    ? selectedMethodId
    : onlyMethodId;
  const activeMethod = methods.find((method) => method.paymentMethodId === activeMethodId) ?? null;

  const { data: quoteData, loading: quoteLoading, error: quoteError } = useInvoicePaymentQuery(
    open ? invoice?.invoiceId : null,
    activeMethodId,
  );
  const quote = activeMethodId ? quoteData?.invoicePaymentQuote ?? null : null;

  const [payInvoice, { loading: paying }] = usePayInvoiceWithSavedMethod();

  // A closed dialog forgets the last attempt entirely.
  useEffect(() => {
    if (open) return;
    setSelectedMethodId(null);
    setErrorMessage(null);
    setChargedCents(null);
  }, [open]);

  if (!invoice) return null;

  const lineItems = invoice.lineItems ?? [];
  const canConfirm = Boolean(activeMethodId && quote && !quoteLoading && !paying);
  const totalHintId = `pay-${invoice.invoiceId}-total-hint`;

  const handleConfirm = async () => {
    if (!activeMethodId || !quote) return;
    setErrorMessage(null);

    try {
      const result = await payInvoice({
        variables: { input: { invoiceId: invoice.invoiceId, paymentMethodId: activeMethodId } },
      });
      const payload = result.data?.payInvoiceWithSavedMethod;
      if (payload?.success) {
        setChargedCents(payload.chargedCents ?? quote.totalCents);
        onPaid?.();
        return;
      }
      setErrorMessage(payload?.userErrors?.[0]?.message ?? 'We could not take that payment.');
    } catch {
      setErrorMessage('We could not take that payment. Please try again.');
    }
  };

  if (chargedCents !== null) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Payment started</DialogTitle>
            <DialogDescription>
              {invoice.memo || 'Invoice'}
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm" role="status">
            We are charging <span className="font-medium tabular-nums">{formatCurrency(chargedCents)}</span>
            {activeMethod ? ` to ${describePaymentMethod(activeMethod)}` : ''}. The invoice shows as paid once the payment settles.
          </p>
          <DialogFooter>
            <Button onClick={() => onOpenChange(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Pay this invoice</DialogTitle>
          <DialogDescription>
            {invoice.memo || 'Invoice'}
            {invoice.dueDate ? ` · Due ${formatDate(invoice.dueDate)}` : ''}
          </DialogDescription>
        </DialogHeader>

        {lineItems.length > 0 && (
          <ul className="space-y-1 border-y py-3 text-sm">
            {lineItems.map((line) => (
              <li key={line.lineItemId} className="flex justify-between gap-4">
                <span>
                  {line.description}
                  {line.person?.firstName ? (
                    <span className="text-muted-foreground"> · {line.person.firstName}</span>
                  ) : null}
                </span>
                <span className="tabular-nums">
                  {line.lineType === 'discount' ? '-' : ''}
                  {formatCurrency(line.amountCents)}
                </span>
              </li>
            ))}
          </ul>
        )}

        {methodsLoading ? (
          <p className="text-sm text-muted-foreground">Loading your saved payment methods…</p>
        ) : methods.length === 0 ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Add a payment method to pay this invoice.</p>
            {familyEnrollmentId ? <AddPaymentMethodButton familyEnrollmentId={familyEnrollmentId} /> : null}
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm font-medium" id={`pay-${invoice.invoiceId}-method-label`}>Choose a payment method</p>
            <RadioGroup
              value={activeMethodId ?? ''}
              onValueChange={setSelectedMethodId}
              aria-labelledby={`pay-${invoice.invoiceId}-method-label`}
            >
              {methods.map((method) => (
                <div key={method.paymentMethodId} className="flex items-center gap-2">
                  <RadioGroupItem value={method.paymentMethodId} id={`pm-${method.paymentMethodId}`} />
                  <Label htmlFor={`pm-${method.paymentMethodId}`} className="font-normal">
                    {describePaymentMethod(method)}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        )}

        {/* The amount is the invoice's own lines; the fee, when there is one, is
            the chosen method's and the total is what will actually be charged. */}
        <dl className="space-y-1 border-t pt-3 text-sm">
          <div className="flex justify-between gap-4">
            <dt>Amount</dt>
            <dd className="tabular-nums">{formatCurrency(quote?.amountCents ?? invoice.amountCents)}</dd>
          </div>
          {quote && quote.feeCents > 0 && (
            <div className="flex justify-between gap-4">
              <dt>{quote.description}</dt>
              <dd className="tabular-nums">{formatCurrency(quote.feeCents)}</dd>
            </div>
          )}
          <div className="flex justify-between gap-4 font-medium">
            <dt>Total</dt>
            <dd className="tabular-nums">
              {quote
                ? formatCurrency(quote.totalCents)
                : activeMethodId && quoteLoading
                  ? <span className="text-muted-foreground font-normal">Calculating…</span>
                  : <span id={totalHintId} className="text-muted-foreground font-normal">Choose a method to see the total</span>}
            </dd>
          </div>
        </dl>

        {quoteError && <p role="alert" className="text-sm text-destructive">We could not price that payment method. Try another or try again.</p>}
        {errorMessage && <p role="alert" className="text-sm text-destructive">{errorMessage}</p>}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={paying}>Cancel</Button>
          {methods.length > 0 && (
            <Button
              onClick={() => { void handleConfirm(); }}
              disabled={!canConfirm}
              aria-describedby={!quote && !(activeMethodId && quoteLoading) ? totalHintId : undefined}
            >
              {paying ? 'Paying…' : quote ? `Pay ${formatCurrency(quote.totalCents)}` : 'Pay'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
